import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDashboard } from '@/context/DashboardContext';
import { TRANCHES } from '@/data/pipeline';
import { STORIES } from '@/data/pipeline';
import { PROGRAMS } from '@/data/seed';
import {
  GRANT_PURPOSE, GRANT_PM, GRANT_DONOR_POC,
  BUDGET_HEADS_SPLIT, GRANT_ATTACHMENTS,
} from '@/data/grantProfile';
import {
  GRANT_KPIS, KPI_CATEGORY_STYLE, fmtKpi, KPI_LOWER_IS_BETTER,
  GRANT_MILESTONES, MILESTONE_STYLE,
} from '@/data/grantKpis';
import { cr, crFull, fmtDate, daysFrom, fmtIN, utilColor } from '@/lib/fmt';
import { Chip, ProgramChip, ProgressBar } from '@/components/UiBits';
import {
  ArrowLeft, Mail, Phone, User, Building2, Calendar, FileText,
  Download, MapPin, ClipboardCheck, BookOpen, FileArchive, Flag, Target as TargetIco,
  CheckCircle2, CircleDashed, Clock, Database
} from 'lucide-react';

const TABS = [
  { key: 'overview',   label: 'Overview'             },
  { key: 'kpis',       label: 'KPIs'                 },
  { key: 'milestones', label: 'Milestones'           },
  { key: 'budget',     label: 'Budget Heads'         },
  { key: 'spoc',       label: 'SPOC'                 },
  { key: 'attachments',label: 'Attachments'          },
  { key: 'compliance', label: 'Reporting Compliance' },
  { key: 'stories',    label: 'Case Studies'         },
];

const statusPill = (health) => (
  health === 'On Track' ? { cls: 'ontrack', dot: '#16A34A', text: 'Active · On Track' } :
  health === 'Watch'    ? { cls: 'pending', dot: '#D97706', text: 'Active · Watch' } :
  health === 'At Risk'  ? { cls: 'atrisk',  dot: '#DC2626', text: 'Active · At Risk' } :
                          { cls: 'closed',  dot: '#6B7280', text: 'Closed' }
);

// Grant start = 1 Apr of FY-start year (e.g. FY 25-26 -> 2025-04-01)
const grantStart = (g) => {
  const m = /(\d{2})-(\d{2})/.exec(g.fy || '');
  return m ? `20${m[1]}-04-01` : null;
};

export default function GrantProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { grants, donors, PROGRAMS: P } = useDashboard();
  const [tab, setTab] = useState('overview');

  const g = grants.find((x) => x.id === id);
  const donor = g ? donors.find((d) => d.id === g.donor) : null;

  if (!g) {
    return (
      <div className="empty-state" data-testid="gp-not-found">
        <div className="empty-title">Grant not found</div>
        <div className="empty-sub">The grant ID <b>{id}</b> is not in the register.</div>
        <button type="button" className="btn-reset" onClick={() => nav('/grants')}>
          <ArrowLeft size={13} /> Back to Grants
        </button>
      </div>
    );
  }

  const s = statusPill(g.status === 'Closed' ? 'Closed' : g.health);
  const utilPct = g.recd ? (g.util / g.recd) * 100 : 0;
  const program = PROGRAMS[g.program];

  return (
    <>
      <div className="mb-3">
        <Link to="/grants" className="inline-flex items-center gap-1.5 text-[12.5px] text-[color:var(--gray-500)] hover:text-[color:var(--llf-gold-text)]" data-testid="gp-back">
          <ArrowLeft size={13} /> Back to Grants &amp; Utilisation
        </Link>
      </div>

      {/* HERO CARD */}
      <div className="card mb-4" style={{ padding: '24px 28px' }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[22px] font-extrabold text-[color:var(--gray-900)] tracking-[-0.3px] leading-tight" data-testid="gp-title">
              {g.name}
            </div>
            <div className="mt-1 font-mono text-[12px] text-[color:var(--gray-500)] tracking-wider">
              {g.id} <span className="mx-1 text-[color:var(--gray-300)]">·</span> Grant Profile
            </div>
          </div>
          <span className={`chip ${s.cls}`} style={{ padding: '5px 12px', fontSize: 12 }} data-testid="gp-status">
            <span className="chip-dot" style={{ background: s.dot }} />
            {s.text}
          </span>
        </div>
      </div>

      {/* TABS */}
      <div className="card mb-4" style={{ padding: '0 20px' }}>
        <div className="flex gap-1 overflow-x-auto no-scrollbar" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              data-testid={`gp-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap px-4 py-3.5 text-[13.5px] font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'text-[color:var(--gray-900)] font-semibold border-[color:var(--llf-gold-deep)]'
                  : 'text-[color:var(--gray-500)] border-transparent hover:text-[color:var(--gray-800)]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* CONTENT */}
      {tab === 'overview'    && <OverviewTab g={g} donor={donor} program={program} utilPct={utilPct} />}
      {tab === 'kpis'        && <KpisTab g={g} />}
      {tab === 'milestones'  && <MilestonesTab g={g} />}
      {tab === 'budget'      && <BudgetTab g={g} />}
      {tab === 'spoc'        && <SpocTab g={g} donor={donor} />}
      {tab === 'attachments' && <AttachmentsTab g={g} />}
      {tab === 'compliance'  && <ComplianceTab g={g} />}
      {tab === 'stories'     && <StoriesTab g={g} donor={donor} PROGRAMS={P} />}
    </>
  );
}

/* ================ OVERVIEW ================ */
function OverviewTab({ g, donor, program, utilPct }) {
  const start = grantStart(g);
  const startStr = start ? new Date(start).toLocaleString('en-IN', { month: 'short', year: 'numeric' }) : '—';
  const endStr   = new Date(g.end).toLocaleString('en-IN', { month: 'short', year: 'numeric' });

  // Instalment schedule: use TRANCHES if any, else auto-generate 4 quarterly instalments
  const existing = TRANCHES.filter((t) => t.grant === g.id);
  const schedule = existing.length ? existing.map((t, i) => ({
    milestone: t.t,
    date: t.status === 'In Bank' ? t.recdOn : t.due,
    amount: t.amt,
    status: t.status === 'In Bank' ? 'Received' : t.status === 'Invoiced' ? 'Invoiced' : 'Scheduled',
  })) : autoSchedule(g);

  const received = schedule.filter((r) => r.status === 'Received').length;

  return (
    <div className="card">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        <Field label="Donor" value={donor?.name || '—'} />
        <Field label="Grantee Partners" value={<>Sahaya Foundation <span className="text-[color:var(--gray-400)]">(lead implementation)</span></>} />
        <Field label="Period" value={<>{startStr} <span className="mx-1 text-[color:var(--gray-400)]">–</span> {endStr}</>} />
        <Field label="Grant Amount" value={<span title={crFull(g.sanct)}>{cr(g.sanct)}</span>} />
        <Field label="Program Pillar" value={<ProgramChip program={g.program} PROGRAMS={PROGRAMS} />} />
        <Field label="Primary State(s)" value={
          <span className="inline-flex flex-wrap gap-1">
            {(g.states || []).map((r) => (
              <span key={r} className="chip closed" style={{ background: 'var(--gray-100)' }}>
                <MapPin size={9} className="mr-1" /> {r}
              </span>
            ))}
          </span>
        } />
        <Field label="Fiscal Year" value={g.fy} />
        <Field label="Reporting Due" value={<>
          {fmtDate(g.report)}
          <span className="ml-2 text-[12px]" style={{ color: daysFrom(g.report) < 0 ? 'var(--red-600)' : 'var(--gray-400)' }}>
            ({daysFrom(g.report) < 0 ? Math.abs(daysFrom(g.report)) + 'd overdue' : 'in ' + daysFrom(g.report) + 'd'})
          </span>
        </>} />
      </div>

      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[color:var(--gray-500)] mb-1.5">Purpose</div>
        <p className="text-[14px] leading-[1.65] text-[color:var(--gray-700)] max-w-[92ch]">
          {GRANT_PURPOSE[g.id] || `Delivery of the ${program?.name} pillar in ${(g.states || []).join(', ')} across ${g.fy}.`}
        </p>
      </div>

      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[color:var(--gray-500)] mb-1.5">Utilisation vs Received</div>
        <div className="flex items-center gap-3">
          <ProgressBar pct={utilPct} color={utilColor(utilPct)} />
          <span className="text-[13px] text-[color:var(--gray-600)]">
            <b className="text-[color:var(--gray-800)]">{cr(g.util)}</b> of {cr(g.recd)} received &middot;
            {' '}committed {cr(g.sanct)}
          </span>
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[color:var(--gray-500)] mb-2">
          Instalment Schedule
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Milestone</th>
                <th>Date</th>
                <th className="num">Payment (₹ Cr)</th>
                <th className="ctr">Status</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((r, i) => (
                <tr key={i}>
                  <td><span className="ent">{r.milestone}</span></td>
                  <td>{fmtDate(r.date)}</td>
                  <td className="num" title={crFull(r.amount)}>{cr(r.amount)}</td>
                  <td className="ctr">
                    {r.status === 'Received'  && <Chip kind="ontrack"><span className="chip-dot" style={{ background: '#16A34A' }} /> Received</Chip>}
                    {r.status === 'Invoiced'  && <Chip kind="info"><span className="chip-dot" style={{ background: '#2563EB' }} /> Invoiced</Chip>}
                    {r.status === 'Scheduled' && <Chip kind="closed"><span className="chip-dot" style={{ background: '#9CA3AF' }} /> Scheduled</Chip>}
                  </td>
                </tr>
              ))}
              <tr>
                <td><b>Total Grant</b></td>
                <td></td>
                <td className="num" style={{ color: 'var(--gray-900)', fontWeight: 700 }} title={crFull(g.sanct)}>{cr(g.sanct)}</td>
                <td className="ctr" style={{ color: 'var(--gray-500)' }}>{received} of {schedule.length} received</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[color:var(--gray-500)] mb-1">{label}</div>
    <div className="text-[15px] font-semibold text-[color:var(--gray-900)]">{value}</div>
  </div>
);

function autoSchedule(g) {
  const start = new Date(grantStart(g));
  const end   = new Date(g.end);
  const q1 = new Date(start.getFullYear(),     start.getMonth() + 3, 30);
  const q2 = new Date(start.getFullYear(),     start.getMonth() + 6, 30);
  const q3 = new Date(start.getFullYear(),     start.getMonth() + 9, 30);
  const q4 = end;
  const per = +(g.sanct / 4).toFixed(2);
  const now = new Date('2026-06-05');
  const st = (d) => d < now ? 'Received' : 'Scheduled';
  return [
    { milestone: 'Tranche 1 · Kick-off',   date: q1.toISOString().slice(0, 10), amount: per, status: st(q1) },
    { milestone: 'Tranche 2 · Mid-review', date: q2.toISOString().slice(0, 10), amount: per, status: st(q2) },
    { milestone: 'Tranche 3 · UC Q3',      date: q3.toISOString().slice(0, 10), amount: per, status: st(q3) },
    { milestone: 'Tranche 4 · Close-out',  date: q4.toISOString().slice(0, 10), amount: +(g.sanct - per * 3).toFixed(2), status: st(q4) },
  ];
}

/* ================ KPIS ================ */
function Sparkline({ values, achieved, target, lowerIsBetter }) {
  if (!values || values.length === 0) return null;
  const w = 100, h = 30, pad = 3;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = (w - pad * 2) / (values.length - 1);
  const pts = values.map((v, i) => [pad + i * step, h - pad - ((v - min) / range) * (h - pad * 2)]);
  const d = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const good = lowerIsBetter ? achieved <= target : achieved >= target;
  const stroke = good ? '#16A34A' : achieved >= target * 0.85 ? '#D97706' : '#DC2626';
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-8 mt-1" preserveAspectRatio="none">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={i === pts.length - 1 ? 2 : 1.4} fill={stroke} />
      ))}
    </svg>
  );
}

function KpisTab({ g }) {
  const kpis = GRANT_KPIS[g.id] || [];

  if (kpis.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <TargetIco size={48} strokeWidth={1.4} className="text-[color:var(--gray-300)]" />
          <div className="empty-title">No KPIs configured yet</div>
          <div className="empty-sub">Grant-specific KPIs are set up jointly with the donor after MoU sign-off.</div>
        </div>
      </div>
    );
  }

  const evalKpi = (k) => {
    const lower = KPI_LOWER_IS_BETTER.has(k.unit);
    if (lower) return k.achieved <= k.target ? 'onTarget' : k.achieved <= k.target * 1.15 ? 'watch' : 'off';
    return k.achieved >= k.target ? 'onTarget' : k.achieved >= k.target * 0.85 ? 'watch' : 'off';
  };
  const summary = kpis.reduce((s, k) => { s[evalKpi(k)]++; return s; }, { onTarget: 0, watch: 0, off: 0 });
  const statusLabel = { onTarget: 'On target', watch: 'Watch', off: 'Off target' };
  const statusRing  = { onTarget: '#16A34A',   watch: '#D97706', off: '#DC2626' };

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="card-title">Grant KPI Scorecard</div>
          <div className="card-sub">
            Contractual outcomes measured against MoU targets · reported quarterly to donor
          </div>
        </div>
        <div className="flex gap-4 text-[12.5px]">
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: '#16A34A' }} /> <b>{summary.onTarget}</b> on target</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: '#D97706' }} /> <b>{summary.watch}</b> watch</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: '#DC2626' }} /> <b>{summary.off}</b> off target</span>
        </div>
      </div>

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: '36%' }}>KPI</th>
              <th>Category</th>
              <th className="num">Target</th>
              <th className="num">Achieved</th>
              <th style={{ width: 180 }}>Progress</th>
            </tr>
          </thead>
          <tbody>
            {kpis.map((k, i) => {
              const lower = KPI_LOWER_IS_BETTER.has(k.unit);
              const pct = lower
                ? Math.min(120, (k.target / Math.max(k.achieved, 1)) * 100)
                : Math.min(120, (k.achieved / k.target) * 100);
              const state = evalKpi(k);
              const ring = statusRing[state];
              const tint = KPI_CATEGORY_STYLE[k.category];

              return (
                <tr key={i} data-testid={`kpi-row-${g.id}-${i}`}>
                  <td>
                    <span className="ent">{k.name}</span>
                    <span className="sub">{k.definition}</span>
                  </td>
                  <td>
                    <span className="chip" style={{ background: tint.bg, color: tint.fg, fontSize: 11, padding: '2px 9px' }}>
                      {k.category}
                    </span>
                  </td>
                  <td className="num">{fmtKpi(k.target, k.unit)}</td>
                  <td className="num" style={{ color: ring, fontWeight: 700 }}>{fmtKpi(k.achieved, k.unit)}</td>
                  <td>
                    <div className="prog" style={{ minWidth: 0 }}>
                      <div className="track"><div className="fill" style={{ width: pct + '%', background: ring }} /></div>
                      <span className="pct">{Math.round(pct)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================ MILESTONES ================ */
function MilestonesTab({ g }) {
  const items = (GRANT_MILESTONES[g.id] || []).slice().sort((a, b) => new Date(a.date) - new Date(b.date));

  if (items.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <Flag size={48} strokeWidth={1.4} className="text-[color:var(--gray-300)]" />
          <div className="empty-title">No milestones scheduled yet</div>
          <div className="empty-sub">Delivery milestones are agreed with the donor at MoU sign-off.</div>
        </div>
      </div>
    );
  }

  const total = items.length;
  const done = items.filter((m) => m.status === 'Done').length;
  const inProg = items.filter((m) => m.status === 'In Progress').length;
  const delayed = items.filter((m) => m.status === 'Delayed').length;
  const pctDone = (done / total) * 100;

  const iconOf = (st) => st === 'Done' ? CheckCircle2 : st === 'In Progress' ? Clock : st === 'Delayed' ? Clock : CircleDashed;

  return (
    <>
      <div className="card mb-4">
        <div className="card-head" style={{ marginBottom: 12 }}>
          <div>
            <div className="card-title">Delivery Milestones</div>
            <div className="card-sub">Contractual delivery timeline · agreed with donor at MoU</div>
          </div>
          <div className="flex gap-4 text-[12.5px]">
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: '#16A34A' }} /> <b>{done}</b> done</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: '#D97706' }} /> <b>{inProg}</b> in progress</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: '#DC2626' }} /> <b>{delayed}</b> delayed</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: '#9CA3AF' }} /> <b>{total - done - inProg - delayed}</b> upcoming</span>
          </div>
        </div>
        <div className="prog">
          <div className="track"><div className="fill" style={{ width: pctDone + '%', background: '#16A34A' }} /></div>
          <span className="pct">{Math.round(pctDone)}%</span>
        </div>
      </div>

      <div className="card">
        <div className="relative pl-6">
          <div className="absolute left-[10px] top-1 bottom-1 w-px bg-[color:var(--gray-200)]" />
          {items.map((m, i) => {
            const st = MILESTONE_STYLE[m.status] || MILESTONE_STYLE.Upcoming;
            const Ico = iconOf(m.status);
            const dd = daysFrom(m.date);
            return (
              <div key={i} className="relative pb-6 last:pb-1" data-testid={`milestone-${g.id}-${i}`}>
                <span
                  className="absolute -left-[19px] top-0 w-5 h-5 rounded-full grid place-items-center"
                  style={{ background: st.bg, color: st.fg, border: `2px solid ${st.dot}` }}
                >
                  <Ico size={11} strokeWidth={2.5} />
                </span>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold text-[color:var(--gray-900)] leading-snug">
                      {m.label}
                    </div>
                    <div className="text-[12px] text-[color:var(--gray-500)] mt-0.5">
                      {fmtDate(m.date)}
                      <span className="mx-1.5 text-[color:var(--gray-300)]">·</span>
                      Owner: <b className="text-[color:var(--gray-700)] font-medium">{m.owner}</b>
                      {m.status !== 'Done' && (
                        <>
                          <span className="mx-1.5 text-[color:var(--gray-300)]">·</span>
                          <span style={{ color: dd < 0 && m.status === 'Delayed' ? 'var(--red-600)' : 'inherit' }}>
                            {dd < 0 ? Math.abs(dd) + 'd ago' : 'in ' + dd + 'd'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <Chip kind={m.status === 'Done' ? 'ontrack' : m.status === 'In Progress' ? 'pending' : m.status === 'Delayed' ? 'atrisk' : 'closed'}>
                    <span className="chip-dot" style={{ background: st.dot }} /> {m.status}
                  </Chip>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ================ BUDGET ================ */
function BudgetTab({ g }) {
  const rows = BUDGET_HEADS_SPLIT.map((h) => {
    const allocated = +(g.sanct * h.pct / 100).toFixed(2);
    // Utilised follows the overall utilisation ratio, with slight variance per head
    const baseRatio = g.recd ? g.util / g.recd : 0;
    const variance = { delivery: 1.02, personnel: 0.98, monitoring: 0.85, overheads: 1.05, admin: 0.75 }[h.key] || 1;
    const utilised  = Math.min(allocated, +(allocated * baseRatio * variance).toFixed(2));
    const pct       = allocated ? (utilised / allocated) * 100 : 0;
    return { ...h, allocated, utilised, pct };
  });

  const totalAlloc = rows.reduce((s, r) => s + r.allocated, 0);
  const totalUtil  = rows.reduce((s, r) => s + r.utilised,  0);

  return (
    <div className="card">
      <div className="mb-4 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="card-title">Budget Heads</div>
          <div className="card-sub">Allocation of {cr(g.sanct)} · MoU line items · utilisation to date</div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border"
            style={{ borderColor: 'var(--green-100)', background: 'var(--green-50)', color: 'var(--green-700)' }}
            title="Utilisation figures are pulled from the Sahaya Tally instance and refreshed nightly."
            data-testid="budget-tally-sync"
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--green-600)' }} />
            <Database size={11} strokeWidth={2.2} />
            Utilised · synced from Tally · 3 Jun 2026, 09:12 IST
          </span>
          <span className="text-[12.5px] text-[color:var(--gray-500)]">
            Total utilised: <b className="text-[color:var(--gray-800)]">{cr(totalUtil)}</b> ({((totalUtil / totalAlloc) * 100).toFixed(0)}%)
          </span>
        </div>
      </div>

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Head</th>
              <th className="num">Allocated</th>
              <th className="num">Utilised</th>
              <th>Utilisation</th>
              <th className="num">Share of grant</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>
                  <span className="ent">{r.name}</span>
                  <span className="sub">{r.desc}</span>
                </td>
                <td className="num" title={crFull(r.allocated)}>{cr(r.allocated)}</td>
                <td className="num" title={crFull(r.utilised)}>{cr(r.utilised)}</td>
                <td><ProgressBar pct={r.pct} color={utilColor(r.pct)} /></td>
                <td className="num">{r.pct.toFixed(0) === '0' ? '—' : `${r.pct.toFixed(0)}%`}<span className="text-[color:var(--gray-400)] text-[11px] ml-1">({r.pct.toFixed(0)}% of head)</span></td>
              </tr>
            ))}
            <tr>
              <td><b>Total</b></td>
              <td className="num" style={{ color: 'var(--gray-900)', fontWeight: 700 }}>{cr(totalAlloc)}</td>
              <td className="num" style={{ color: 'var(--gray-900)', fontWeight: 700 }}>{cr(totalUtil)}</td>
              <td>
                <ProgressBar pct={(totalUtil / totalAlloc) * 100} color={utilColor((totalUtil / totalAlloc) * 100)} />
              </td>
              <td className="num" style={{ color: 'var(--gray-500)' }}>100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================ SPOC ================ */
function SpocCard({ title, icon: Ico, entity, tint, poc }) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="px-5 py-3 border-b border-[color:var(--gray-100)] flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: tint.bg, color: tint.fg }}>
          <Ico size={15} strokeWidth={2} />
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest font-semibold text-[color:var(--gray-500)]">{title}</div>
          <div className="text-[13.5px] font-semibold text-[color:var(--gray-900)]">{entity}</div>
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <Field label="Name"  value={poc.name} />
        <Field label="Role"  value={poc.role} />
        <Field label="Email" value={
          poc.email !== '—' ?
            <a href={`mailto:${poc.email}`} className="inline-flex items-center gap-1.5 text-[color:var(--gray-800)] hover:text-[color:var(--llf-gold-text)]">
              <Mail size={12} /> {poc.email}
            </a>
          : '—'
        } />
        {poc.phone && (
          <Field label="Phone" value={
            <span className="inline-flex items-center gap-1.5">
              <Phone size={12} /> {poc.phone}
            </span>
          } />
        )}
      </div>
    </div>
  );
}

function SpocTab({ g, donor }) {
  const pm  = GRANT_PM[g.id] || { name: '—', role: '—', email: '—', phone: '—' };
  const dp  = GRANT_DONOR_POC[g.id] || { name: '—', role: '—', email: '—' };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <SpocCard title="Donor-side SPOC"   icon={Building2} entity={donor?.name || '—'} poc={dp}
        tint={{ bg: 'var(--llf-gold-soft)', fg: 'var(--llf-gold-text)' }} />
      <SpocCard title="Grantee-side SPOC" icon={User}      entity="Sahaya Foundation"  poc={pm}
        tint={{ bg: 'var(--green-100)',      fg: 'var(--green-700)' }} />
    </div>
  );
}

/* ================ ATTACHMENTS ================ */
const kindIcon = (k) =>
  k === 'MoU'                 ? { fg: '#7C3AED', bg: '#F3E8FF' } :
  k === 'Proposal'            ? { fg: '#2563EB', bg: '#DBEAFE' } :
  k === 'Sanction Letter'     ? { fg: '#B45309', bg: '#FEF3C7' } :
  k === 'Utilisation Cert.'   ? { fg: '#15803D', bg: '#DCFCE7' } :
  k === 'Financial'           ? { fg: '#0F766E', bg: '#CCFBF1' } :
  k === 'Progress Report'     ? { fg: '#1D4ED8', bg: '#DBEAFE' } :
  k === 'Media'               ? { fg: '#B0367B', bg: '#FBE7F3' } :
                                { fg: '#B91C1C', bg: '#FFE4E6' };

function AttachmentsTab({ g }) {
  const files = GRANT_ATTACHMENTS(g.id);
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="card-title">Attachments</div>
          <div className="card-sub">{files.length} documents · MoU, UCs, financials, media</div>
        </div>
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr><th>Document</th><th>Kind</th><th>Uploaded</th><th className="num">Size</th><th></th></tr>
          </thead>
          <tbody>
            {files.map((f) => {
              const tint = kindIcon(f.kind);
              return (
                <tr key={f.name}>
                  <td>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-7 h-7 grid place-items-center rounded-md" style={{ background: tint.bg, color: tint.fg }}>
                        <FileText size={13} strokeWidth={2} />
                      </span>
                      <span className="ent">{f.name}</span>
                    </span>
                  </td>
                  <td><span className="chip closed" style={{ color: tint.fg, background: tint.bg }}>{f.kind}</span></td>
                  <td>{fmtDate(f.uploaded)}</td>
                  <td className="num">{f.size}</td>
                  <td>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); }}
                      className="inline-flex items-center gap-1 text-[12px] font-semibold text-[color:var(--gray-500)] hover:text-[color:var(--llf-gold-text)] cursor-pointer"
                    >
                      <Download size={12} /> Download
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================ COMPLIANCE ================ */
function ComplianceTab({ g }) {
  const start = new Date(grantStart(g));
  const now   = new Date('2026-06-05');
  const nextDue = new Date(g.report);

  // Generate quarterly schedule from start to end
  const items = [];
  let cursor = new Date(start.getFullYear(), start.getMonth() + 3, 0);  // end of Q1
  const end = new Date(g.end);
  let q = 1;
  const fyLabel = (d) => `Q${((q - 1) % 4) + 1} ${g.fy}`;
  while (cursor <= end && q <= 12) {
    const due = new Date(cursor);
    let status;
    if (due < now && due < nextDue) status = 'Submitted';
    else if (due.toDateString() === nextDue.toDateString()) status = g.uc; // reflect current UC status
    else if (due < now)                                     status = g.uc === 'Overdue' ? 'Overdue' : 'Submitted';
    else                                                    status = 'Scheduled';
    items.push({ label: `${fyLabel(due)} · Utilisation Certificate`, date: due.toISOString().slice(0, 10), kind: 'UC', status });
    q++;
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 0);
  }
  // Sort — upcoming first, then past
  items.sort((a, b) => new Date(a.date) - new Date(b.date));

  const pill = (st) => (
    st === 'Overdue'   ? { cls: 'atrisk',  dot: '#DC2626' } :
    st === 'Due'       ? { cls: 'pending', dot: '#D97706' } :
    st === 'Submitted' ? { cls: 'ontrack', dot: '#16A34A' } :
                         { cls: 'closed',  dot: '#9CA3AF' }
  );

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="card-title">Reporting Compliance</div>
          <div className="card-sub">Quarterly utilisation certificates &amp; donor reports</div>
        </div>
        <div className="text-[12.5px] text-[color:var(--gray-500)]">
          Overall UC status: <Chip kind={g.uc}>{g.uc}</Chip>
        </div>
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr><th>Milestone</th><th>Due Date</th><th className="ctr">Kind</th><th className="ctr">Status</th></tr>
          </thead>
          <tbody>
            {items.map((r, i) => {
              const p = pill(r.status);
              return (
                <tr key={i}>
                  <td><span className="ent">{r.label}</span></td>
                  <td>{fmtDate(r.date)}
                    <span className="text-[11px] ml-1" style={{ color: daysFrom(r.date) < 0 ? 'var(--gray-500)' : 'var(--gray-400)' }}>
                      ({daysFrom(r.date) < 0 ? Math.abs(daysFrom(r.date)) + 'd ago' : 'in ' + daysFrom(r.date) + 'd'})
                    </span>
                  </td>
                  <td className="ctr"><Chip kind="closed">{r.kind}</Chip></td>
                  <td className="ctr">
                    <Chip kind={p.cls}><span className="chip-dot" style={{ background: p.dot }} /> {r.status}</Chip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================ STORIES ================ */
function StoriesTab({ g, donor, PROGRAMS: P }) {
  const items = STORIES.items.filter((s) => s.grant === g.id);
  return (
    <div>
      {items.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <BookOpen size={48} strokeWidth={1.4} className="text-[color:var(--gray-300)]" />
            <div className="empty-title">No case studies filed yet for this grant</div>
            <div className="empty-sub">
              Field reporters have not yet published an editorial note for <b>{g.name}</b>.
              Case studies are added by the Communications team quarterly.
            </div>
          </div>
        </div>
      ) : (
        <div className="ed-grid">
          {items.map((s, i) => (
            <article key={i} className="ed-card">
              <div className="ed-img">
                <img src={s.img} alt={s.title} loading="lazy" />
                <div className="ed-sector">{PROGRAMS[s.program].name}</div>
              </div>
              <div className="ed-body">
                <div className="overline">{fmtDate(s.date)} · {s.district}, {s.state}</div>
                <div className="ed-title">{s.title}</div>
                <div className="ed-excerpt">{s.excerpt}</div>
                <div className="ed-quote">
                  “{s.quote}”
                  <span className="who">— {s.who}</span>
                </div>
                <div className="ed-meta">
                  <span className="k">Donor</span><span className="v">{s.donor}</span>
                  <span className="k">Grant</span><span className="v">{s.grantName}</span>
                </div>
              </div>
              <div className="ed-foot">
                <div className="fn">Field note · <b>{s.author}</b></div>
                <ProgramChip program={s.program} PROGRAMS={P} />
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
